import { Injectable } from "@angular/core";
import { AsyncSubject, combineLatest } from "rxjs";
import { MEError } from "../../error/me-error";
import { MEAPIResponse, MEEntityAPIResponseHandler, ResponseCode, BasicAsyncResponseSuccessHandler } from "../api/me-api-response";
import { MEAPIService } from "../api/me-api.service";
import { ExternalSettings, WebLogo } from "./external-settings.interface";
import { TranslateService } from "@ngx-translate/core";
import { LocalStorageType } from "src/app/system/utility/local-storage.service";
import { environment } from "src/environments/environment";
import { MEErrorService } from "../../error/me-error.service";
import { CurrencyUtilityService } from "src/app/system/utility/currency-utility.service";
import { PlatformService } from "src/app/system/utility/platform.service";
import { EnvironmentService } from "src/app/system/utility/environment.service";
import { CountryUtilityService } from "src/app/system/utility/country-utility.service";
import { BankTemplate } from "../bank-template/bank-template-bean";
import { DateTimeService } from "src/app/system/utility/datetime.service";
import { AppService } from "src/app/system/utility/app.service";
import { UIUtilityService } from "src/app/system/utility/ui-utility.service";
import { NetworkService } from "src/app/system/utility/network.service";
import { StringUtilityService } from "src/app/system/utility/string-utility.service";
import { logger } from "../logging/logging.service";
import { SkinService } from "src/app/system/utility/skin.service";
import { OverlayContainer } from '@angular/cdk/overlay';
import { AppModeService } from "src/app/system/utility/app-mode.service";

@Injectable({
  providedIn: 'root'
})
export class ExternalSettingsService {

  private environemnt = environment;

  private readonly API_EXTERNAL_SETTINGS: string = MEAPIService.BASE_URL + '/customer/externalSetting?isWeb=true';
  private readonly API_WEB_BACKGROUND_IMAGE: string = MEAPIService.BASE_URL + '/external/viewimage/loginBackground';
  private readonly API_APP_BACKGROUND_IMAGE: string = MEAPIService.BASE_URL + '/external/viewimage/app/loginBackground';
  private readonly API_FAVICON: string = MEAPIService.BASE_URL + '/resources/%INSITUTION_ID%/web-portal/favicon/favicon.png';
  private readonly API_APP_VIDEO_BACKGROUND: string = MEAPIService.BASE_URL + '/resources/%INSITUTION_ID%/web-portal/video/app-login-background.mp4';
  private readonly API_WEB_VIDEO_BACKGROUND: string = MEAPIService.BASE_URL + '/resources/%INSITUTION_ID%/web-portal/video/web-login-background.mp4';

  private readonly DEFAULT_LANGUAGE: string = 'en';
  private static isExternalSettingsAvailable = false;

  public readonly externalSettings$: AsyncSubject<ExternalSettings> = new AsyncSubject<ExternalSettings>();
  private _externalSettings?: ExternalSettings;

  constructor(
    private srvApi: MEAPIService,
    private srvTranslate: TranslateService,
    private srvError: MEErrorService,
    private srvApp: AppService,
    private srvNetwork: NetworkService,
    private srvAppMode: AppModeService,
    public overlayContainer: OverlayContainer
  ) {
    // Wait for App Service to be initialzied
    this.srvApp.isInitialized.subscribe((isOkay: boolean) => {
      if (!isOkay) {
        this.markExternalSettingUnavailable();
        return;
      };
      this.init();
    });
  }

  private init() {
    // Set the current and default language
    this.srvTranslate.use(this.DEFAULT_LANGUAGE);
    this.srvTranslate.setDefaultLang(this.DEFAULT_LANGUAGE);

    if (this.srvNetwork.isConnected) {
      combineLatest([
        this.getExternalSettings()
      ]).subscribe(([externalSettings]) => {
        this.initExternalSettings(externalSettings);
      });
    }
    else {
      this.markExternalSettingUnavailable('No Internet Connection');
    }
  }

  private getExternalSettings(): AsyncSubject<ExternalSettings> {
    let ret = new AsyncSubject<ExternalSettings>();

    let responseHandlers = [];
    responseHandlers.push(new BasicAsyncResponseSuccessHandler<ExternalSettings>(ret));
    responseHandlers.push(new SystemUnavailableExternalSettingsHandler(this));

    this.srvApi.Get<ExternalSettings>(this.API_EXTERNAL_SETTINGS, responseHandlers).subscribe(isOkay => {
      if (!isOkay) {
        this.markExternalSettingUnavailable();
      }
    });

    return ret;
  }

  /**
   * Callback from Success Handler
   */
  private async initExternalSettings(externalSettings: ExternalSettings) {
    this._externalSettings = externalSettings;
    this.srvAppMode.initMode({ demoEnabled: externalSettings.globalVariables.demoTextMessageEnable });

    // Initialize Favicon
    await this.initializeFavIcon();

    // Initialize User Language
    await this.initializeLaugnage();

    // Development Override
    await this.initializeDevelopment();

    // Initialize Platform Environment
    await this.initPlatformEnvironment();

    // Initialize Dependencies Library
    await this.initializeLibraries();

    // Mark the application is normal
    await this.markExternalSettingAvailable();
  }

  private initializeLibraries() {
    CurrencyUtilityService.init(this._externalSettings!, this.srvTranslate);
    CountryUtilityService.init(this._externalSettings!);
    DateTimeService.init(this.srvTranslate, this._externalSettings);
    UIUtilityService.init(this._externalSettings!, this.overlayContainer);
    SkinService.init(this._externalSettings!);
  }

  private initializeFavIcon() {
    if (this._externalSettings!.web.instititionId) {
      let favIconPath = this.API_FAVICON.replace('%INSITUTION_ID%', this._externalSettings!.web.instititionId);
      document.getElementsByTagName('link')[0].setAttribute("href", favIconPath);
    }
  }

  private initializeLaugnage() {
    let defaultLang = this.DEFAULT_LANGUAGE + ((this._externalSettings!.web.bankOrCreditUnion === 'creditUnion') ? '-cu' : '');
    let data = localStorage.getItem(LocalStorageType.LANGUAGE);
    this.srvTranslate.use(data ?? defaultLang);
  }

  private async initPlatformEnvironment() {

    this._externalSettings!.web.webLogo = StringUtilityService.clone(this._externalSettings!.web.logo!) as WebLogo;

    if (PlatformService.isBrowser()) {
      let instititionId = this._externalSettings!.web.instititionId!;
      this._externalSettings!.web.appVideoBackground = this.API_APP_VIDEO_BACKGROUND.replace('%INSITUTION_ID%', instititionId); // For Desktop Web
      this._externalSettings!.web.webVideoBackground = this.API_WEB_VIDEO_BACKGROUND.replace('%INSITUTION_ID%', instititionId); // For Mobile Responsive Web
      this._externalSettings!.web.siteBackground = this.API_WEB_BACKGROUND_IMAGE;
      this._externalSettings!.web.loginBackground = this.API_WEB_BACKGROUND_IMAGE;
      this._externalSettings!.web.menuBackground = this.API_WEB_BACKGROUND_IMAGE;
    }
    if (PlatformService.isMobile()) {
      this._externalSettings!.web.siteBackground = 'assets/images/customization/bg_main.jpg';
      this._externalSettings!.web.menuBackground = 'assets/images/customization/bg_menu.jpg';
      await this.initMobileLoginPage(); // Pre-fetch the server image 

      this._externalSettings!.web.webLogo.image = EnvironmentService.getBaseUrl() + this._externalSettings!.web.webLogo.image;
      this._externalSettings!.web.logo.image = 'assets/images/customization/logo_db.png';

      if (this._externalSettings!.web.bannerLogin) this._externalSettings!.web.bannerLogin.image = EnvironmentService.getBaseUrl() + this._externalSettings!.web.bannerLogin.image;
      if (this._externalSettings!.web.viewImages.secureicon) this._externalSettings!.web.viewImages.secureicon = this._externalSettings!.web.viewImages.secureicon.map((icon) => { icon.image = EnvironmentService.getBaseUrl() + icon.image!; return icon; });
    }
  }

  private initializeDevelopment() {
    if (this.environemnt.production) return;
  }

  public static isHeritage(externalSettings: ExternalSettings): boolean {
    let heritageBanks = [BankTemplate.HERITAGE_DOM, BankTemplate.HERITAGE_INT];
    return heritageBanks.includes(externalSettings.templates as BankTemplate);
  }

  private markExternalSettingAvailable() {
    ExternalSettingsService.isExternalSettingsAvailable = true;
    this.externalSettings$.next(this._externalSettings!);
    this.externalSettings$.complete();
  }

  /**
   * Set the Video Embedded Video Page (Further check video flag)
   * Set the Mobile Login Page with pre-fetch background image
   * Fallback to default image if the pre-fetch fails
   */
  private async initMobileLoginPage() {
    this._externalSettings!.web.appVideoBackground = 'assets/videos/customization/loginBackground.mp4';
    this._externalSettings!.web.loginBackground = 'assets/images/customization/bg_login.jpg';
    let backgroundImage = (EnvironmentService.getBaseUrl() + this.API_APP_BACKGROUND_IMAGE);
    try {
      let response = await fetch(backgroundImage);
      if (!response.ok || !response.headers.get('content-type')?.startsWith('image/')) {
        logger.debug('Response not ok or not an image, failed to load mobile login background image from server: ' + backgroundImage);
      }
      else {
        let blob = await response.arrayBuffer();
        if (blob.byteLength > 0) {
          this._externalSettings!.web.loginBackground = backgroundImage;
        }
      }
    } catch (error) {
      logger.debug('Failed to fetch mobile login background image from server: ' + backgroundImage, error);
      return;
    }
  }

  private markExternalSettingUnavailable(message: string = '') {
    ExternalSettingsService.isExternalSettingsAvailable = false;
    if (message) {
      this.srvError.systemUnAvailableError.next({ message: message });
    }
    this.srvError.systemAvailable.next(false);
    this.externalSettings$.complete();
  }

  public handleSystemUnavailable(response: MEAPIResponse<ExternalSettings>) {
    this._externalSettings = response.entity;
    this.externalSettings$.next(response.entity);
    this.markExternalSettingUnavailable(response.message);
  }

  public static isAvailable(): boolean {
    return ExternalSettingsService.isExternalSettingsAvailable;
  }

}

class SystemUnavailableExternalSettingsHandler implements MEEntityAPIResponseHandler<ExternalSettings> {
  public readonly code: ResponseCode = ResponseCode.SYSTEM_UNAVAILABLE;

  constructor(private srvExternalSettings: ExternalSettingsService) { }

  handle(response: MEAPIResponse<ExternalSettings>): boolean | MEError {
    this.srvExternalSettings.handleSystemUnavailable(response);
    return false;
  }
}